import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { ArtistCard } from "@/components/ArtistCard";
import { PostCard } from "@/components/PostCard";
import { StoriesBar } from "@/components/StoriesBar";
import { LiveBanner } from "@/components/LiveBanner";
import { BoostedBanner } from "@/components/BoostedBanner";
import { Search, TrendingUp, Sparkles, Rocket, Music2, Users, Play, Shuffle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { GenreFilter } from "@/components/GenrePicker";
import { GENRE_FAMILIES } from "@/config/genres";
import { NewArtistsBanner } from "@/components/NewArtistsBanner";

export default function DiscoverPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("artists");
  const [artists, setArtists] = useState([]);
  const [posts, setPosts] = useState([]);
  const [search, setSearch] = useState("");
  const [genre, setGenre] = useState("Tous");
  const [loading, setLoading] = useState(true);
  const [myTopFanIds, setMyTopFanIds] = useState([]);
  const [featuredPlaylists, setFeaturedPlaylists] = useState([]);

  const fetchArtists = useCallback(async () => {
    try {
      const params = { sort: "trending", limit: 20 };
      if (genre !== "Tous") {
        const familyGenres = GENRE_FAMILIES[genre];
        if (familyGenres) {
          params.genre = familyGenres.join(",");
        } else {
          params.genre = genre;
        }
      }
      if (search) params.search = search;
      const res = await api.get("/artists", { params });
      setArtists(res.data.artists);
    } catch (err) {
      console.error(err);
    }
  }, [api, genre, search]);

  const fetchPosts = useCallback(async () => {
    try {
      const params = { limit: 20 };
      if (genre !== "Tous") params.genre = genre;
      const res = await api.get("/feed/discover", { params });
      setPosts(res.data.posts);
    } catch (err) {
      console.error(err);
    }
  }, [api, genre]);

  const fetchTopFanStatus = useCallback(async () => {
    try {
      const res = await api.get("/topfan/my/status");
      setMyTopFanIds((res.data.top_fan_for || []).map(t => t.artist_id));
    } catch {
      setMyTopFanIds([]);
    }
  }, [api]);

  const fetchFeaturedPlaylists = useCallback(async () => {
    try {
      const res = await api.get("/playlists/featured");
      setFeaturedPlaylists(res.data.playlists || []);
    } catch (err) { console.error(err); }
  }, [api]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchArtists(), fetchPosts(), fetchTopFanStatus(), fetchFeaturedPlaylists()]).finally(() => setLoading(false));
  }, [fetchArtists, fetchPosts, fetchTopFanStatus, fetchFeaturedPlaylists]);

  const handleFollow = async (artistId) => {
    try {
      const res = await api.post(`/artists/${artistId}/follow`);
      setArtists((prev) =>
        prev.map((a) =>
          a.id === artistId
            ? { ...a, is_following: res.data.following, fan_count: res.data.fan_count }
            : a
        )
      );
    } catch (err) {
      console.error(err);
    }
  };

  const handleLike = async (postId) => {
    const res = await api.post(`/posts/${postId}/like`);
    setPosts((prev) =>
      prev.map((p) =>
        p.id === postId
          ? { ...p, is_liked: res.data.liked, likes_count: res.data.likes_count }
          : p
      )
    );
  };

  const handleReact = async (postId, reactionType) => {
    const res = await api.post(`/posts/${postId}/react`, { reaction_type: reactionType });
    setPosts((prev) =>
      prev.map((p) =>
        p.id === postId
          ? { ...p, user_reaction: res.data.reaction_type, reactions: res.data.reactions }
          : p
      )
    );
    return res.data;
  };

  return (
    <div className="py-6 space-y-6" data-testid="discover-page">
      {/* Header */}
      <div>
        <h1 className="font-heading font-bold text-2xl text-foreground mb-1">Explorer</h1>
        <p className="text-sm text-muted-foreground">Decouvre les artistes du moment</p>
      </div>

      {/* Bandeau Nouveaux Artistes - tres visible en haut */}
      <NewArtistsBanner />

      {/* Lives en cours */}
      <LiveBanner />

      {/* Stories */}
      <StoriesBar />

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Rechercher un artiste..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 bg-card border-border rounded-full text-card-foreground placeholder:text-muted-foreground focus:border-[#FF6B35] focus:ring-1 focus:ring-[#FF6B35] h-11"
          data-testid="discover-search"
        />
      </div>

      {/* Genre filters */}
      <GenreFilter selected={genre} onChange={setGenre} />

      {/* Boosted Posts - Ad Placement */}
      <BoostedBanner placement="discover" />

      {/* Playlists de la semaine */}
      {featuredPlaylists.length > 0 && (
        <div data-testid="featured-playlists">
          <h2 className="font-heading font-bold text-base text-foreground mb-2 flex items-center gap-2">
            <Music2 className="w-4 h-4 text-[#FF6B35]" /> Playlists de la semaine
          </h2>
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-hide">
            {featuredPlaylists.map((pl) => (
              <div
                key={pl.id}
                onClick={() => navigate(`/playlists/${pl.id}`)}
                className="shrink-0 w-44 bg-card border border-border rounded-2xl p-3 cursor-pointer hover:border-[#FF6B35]/40 hover:shadow-md transition-all group"
                data-testid={`featured-playlist-${pl.id}`}
              >
                <div className="w-full h-20 rounded-xl bg-gradient-to-br from-[#FF6B35]/20 via-[#7000FF]/10 to-[#FF8FA3]/20 flex items-center justify-center mb-2">
                  <Play className="w-8 h-8 text-[#FF6B35] group-hover:scale-110 transition-transform" />
                </div>
                <p className="font-heading font-bold text-xs text-card-foreground truncate">{pl.title}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">{pl.tracks?.length || 0} titres</p>
                <div className="flex items-center gap-1 mt-1">
                  {pl.collaborative && <Users className="w-2.5 h-2.5 text-[#FF6B35]" />}
                  <span className="text-[9px] text-muted-foreground truncate">par {pl.creator_pseudo}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-muted rounded-full p-1 border border-border">
        <button
          onClick={() => setTab("artists")}
          className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
            tab === "artists" ? "bg-[#FF6B35] text-white" : "text-muted-foreground"
          }`}
          data-testid="tab-artists"
        >
          <TrendingUp className="w-4 h-4" />
          Artistes
        </button>
        <button
          onClick={() => setTab("posts")}
          className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
            tab === "posts" ? "bg-[#FF6B35] text-white" : "text-muted-foreground"
          }`}
          data-testid="tab-posts"
        >
          <Sparkles className="w-4 h-4" />
          Publications
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-foreground/5 rounded-3xl h-24 animate-pulse" />
          ))}
        </div>
      ) : tab === "artists" ? (
        <div className="space-y-3">
          {artists.length === 0 ? (
            <div className="text-center py-16">
              <TrendingUp className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">Aucun artiste trouve</p>
              <p className="text-xs text-muted-foreground mt-1">Sois le premier a t'inscrire en tant qu'artiste !</p>
            </div>
          ) : (
            artists.map((a) => (
              <ArtistCard key={a.id} artist={a} onFollow={handleFollow} isTopFan={myTopFanIds.includes(a.id)} />
            ))
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {posts.length === 0 ? (
            <div className="text-center py-16">
              <Sparkles className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">Aucune publication</p>
              <p className="text-xs text-muted-foreground mt-1">Les artistes n'ont pas encore publie</p>
            </div>
          ) : (
            posts.map((p) => (
              <PostCard key={p.id} post={p} onLike={handleLike} onReact={handleReact} onDelete={(id) => setPosts((prev) => prev.filter((x) => x.id !== id))} onUpdate={(id, updated) => setPosts((prev) => prev.map((x) => x.id === id ? { ...x, ...updated } : x))} onHide={(id) => setPosts((prev) => prev.filter((x) => x.id !== id))} onBlock={(artistId) => setPosts((prev) => prev.filter((x) => x.artist_id !== artistId))} />
            ))
          )}
        </div>
      )}
    </div>
  );
}
